<template>
    <div id="pickup-detail">
        <v-card class="customer-info">
            <div class="customer-info__header">
                <div class="customer-info__details">
                    <p v-if="hasName">{{ customerInfo.fullName }}</p>
                    <p v-if="hasExternalRef">{{ customerInfo.externalRef }}</p>
                    <p v-if="hasAddress1">{{ customerInfo.address1 }}</p>
                    <p v-if="hasAddress2">{{ customerInfo.address2 }}</p>
                </div>
                <div class="customer-info__scan">
                    <ClInput
                        ref="scannedItem"
                        v-model.trim="models.scannedItem"
                        placeholder="Scan Item ID"
                        @keydown.enter.native="goButtonHandler()"

                    />
                    <ClButton
                        :rounded="false"
                        :disabled="!models.scannedItem"
                        @click="goButtonHandler()"
                    >
                    Go
                    </ClButton>
                </div>
            </div>
            <div class="customer-info__body">
                <ClTableSimple
                    :items="customerInfo.itemsList"
                    :headers="headers"
                >
                    <template #[`item.scanned`] = "{ item }">
                        <v-icon>{{  item.scanned ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline' }}</v-icon>
                    </template>
                </ClTableSimple>
            </div>
            <div class="customer-info__footer">
                <div class="customer-info__actions--left">
                    <ClButton
                        :rounded="false"
                        type="secondary"
                        @click="displayNotesModal()"
                    >
                        Notes &nbsp;<span v-if="hasText"><v-icon small>mdi-message-alert-outline</v-icon></span>
                    </ClButton>
                    <ClButton
                        :rounded="false"
                        type="secondary"
                        @click="displayPictureModal()"
                    >
                        Photo &nbsp;<span v-if="hasPicture"><v-icon small>mdi-message-alert-outline</v-icon></span>
                    </ClButton>
                </div>
                <div>
                    {{ totalScanned }} of {{ customerInfo.itemsList.length }}
                </div>
                <div class="customer-info__actions--right">
                    <ClButton
                        :rounded="false"
                        @click="closeDetail()"
                    >
                        Done
                    </ClButton>
                </div>
            </div>
        </v-card>
        <!-- Note Modal -->
        <ModalNotes
            v-model="showNotesModal"
            :notes="models.notes"
            @addNotes="( note ) => addNotes( note )"
            @close="hideNotesModal"
        />
        <!-- Picture Modal -->
        <ModalPictureSelection
            v-model="showPictureModal"
            :image="models.imageInfo.bgImageData"
            @updatePicture="( data ) => updatePicture( data )"
            @clearPicture="clearPicture()"
            @close="hidePictureModal"
        />
    </div>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Api
import LogisticsApi from '@/api/LogisticsApi';

// Utils
import ErrorUtil from '@/utils/ErrorUtil';
import ValidationUtil from '@/utils/ValidationUtil';
import AudioUtil, { soundNames } from '@/utils/AudioUtil';

// Routes
import MailPickupRoute from '@/router/routes/Mail/routes/PickUp';

// Components
import ModalNotes from '@/components/modal/ModalNotes';
import ModalPictureSelection from '@/components/modal/ModalPictureSelection';

export default {
    name: 'PickupDetail',
    components: {
        ModalNotes,
        ModalPictureSelection,
    },
    data() {
        return {
            showMessageModal: false,
            showNotesModal: false,
            showPictureModal: false,
            inFlight: false,

            totalScanned: 0,
            tableInfo: {
                currentPage: 1,
                perPage: 10,
            },

            customerInfo: {
                id: '',
                fullName: '',
                address1: '',
                address2: '',
                externalRef: null,
                itemsList: [],
            },

            models: {
                scannedItem: '',
                notes: '',
                imageInfo: {
                    bgImageData: [],
                    hash: [],
                },
            },
            headers: [
                {
                    text: 'Scanned',
                    value: 'scanned',
                },
                {
                    text: 'Type',
                    value: 'mailTypeFriendly',
                },
                {
                    text: 'Recipient',
                    value: 'customerFullName',
                },
                {
                    text: 'Tracking Number',
                    value: 'refNum',
                },
                {
                    text: 'Locator',
                    value: 'locatorDescription',
                },
            ],
        };
    },
    computed: {
        ...mapGetters( {
            websiteId: 'Website/id',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
            isPickupPhotoRequired: ( state ) => state.Mail.Settings.requirements.pickupPhoto,
        } ),

        /** @returns { Boolean } */
        hasAllRouteParams() {
            return !!this.$route.params
                && this.$route.params.id
                && this.$route.params.detailParams
                && this.$route.params.detailParams.customerId
                && this.$route.params.detailParams.itemsList;
        },

        /** @returns { Boolean } */
        hasItems() {
            return this.customerInfo.itemsList.length
                && ( this.customerInfo.itemsList.length > 0 )
                && ( this.totalScanned > 0 );
        },
        /** @returns { Boolean } */
        hasText() {
            return !!this.models.notes;
        },
        /** @returns { Number } */
        displayPhotoCount() {
            return this.models.imageInfo.bgImageData.length;
        },
        /** @returns { Boolean } */
        hasPicture() {
            return ( this.displayPhotoCount > 0 );
        },
        /** @returns { Boolean } */
        hasName() {
            return ( this.customerInfo.fullName.length > 0 );
        },
        /** @returns { Boolean } */
        hasExternalRef() {
            return ( this.customerInfo.externalRef !== null );
        },
        /** @returns { Boolean } */
        hasAddress1() {
            return ( this.customerInfo.address1.length > 0 );
        },
        /** @returns { Boolean } */
        hasAddress2() {
            return ( this.customerInfo.address2.length > 0 );
        },
    },
    async mounted() {
        if ( this.hasAllRouteParams ) {
            this.customerInfo.id = this.$route.params.detailParams.customerId.toString();
            this.customerInfo.fullName = this.$route.params.detailParams.fullName;
            this.customerInfo.address1 = this.$route.params.detailParams.address1 || '';
            this.customerInfo.address2 = this.$route.params.detailParams.address2 || '';
            this.customerInfo.externalRef = this.$route.params.detailParams.externalRef;
            this.customerInfo.itemsList = this.$route.params.detailParams.itemsList;
            this.focusScannedItem();
            await this.$store.dispatch( 'Mail/Settings/get' );
        } else if ( this.$route.params.id ) {
            await this.loadCustomerData( this.$route.params.id );
        } else {
            this.$router.push( { path: MailPickupRoute.path } );
        }
    },
    methods: {
        async loadCustomerData( customerId ) {
            this.customerInfo.id = customerId.toString();
            try {
                const { data } = await LogisticsApi.getCustomerPickupList( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    customerId,
                } );
                this.customerInfo.itemsList = data.mailSearchListDTO
                    ? data.mailSearchListDTO.map( ( item ) => ( { ...item, scanned: false } ) )
                    : [];
                if ( this.customerInfo.itemsList.length > 0 ) {
                    this.customerInfo.fullName = this.customerInfo.itemsList[ 0 ].customerFullName || '';
                }
                this.focusScannedItem();
                await this.$store.dispatch( 'Mail/Settings/get' );
            } catch ( error ) {
                this.$store.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( error ),
                    type: 'error',
                    duration: 5000,
                } );
                this.$router.push( { path: MailPickupRoute.path } );
            }
        },
        calculateCurrentPage( index ) {
            return ( Math.floor( index / this.tableInfo.perPage ) ) + 1;
        },
        async goButtonHandler() {
            if ( this.customerInfo.itemsList.find( ( item ) => item.refNum.toLowerCase() === this.models.scannedItem.toString().toLowerCase() ) ) {
                await AudioUtil.playSound( soundNames.SUCCESS_SHORT );
                const index = this.customerInfo.itemsList.findIndex( ( i ) => i.refNum.toLowerCase() === this.models.scannedItem.toString().toLowerCase() );
                this.customerInfo.itemsList[ index ].scanned = true;
                this.totalScanned = this.customerInfo.itemsList.filter( ( n ) => n.scanned === true ).length;
                this.tableInfo.currentPage = this.calculateCurrentPage( index );
                this.models.scannedItem = '';
                this.focusScannedItem();
            } else {
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
                this.$store.dispatch( 'snackbar', {
                    message: `Item ${ this.models.scannedItem } is not for ${ this.customerInfo.fullName }`,
                    type: 'error',
                    duration: 5000,
                } );
                this.models.scannedItem = '';
            }
        },
        displayNotesModal() {
            this.showNotesModal = true;
        },
        displayPictureModal() {
            this.showPictureModal = true;
        },
        hideNotesModal() {
            this.showNotesModal = false;
            this.focusScannedItem();
        },
        addNotes( note ) {
            this.models.notes = note;
        },
        updatePicture( data ) {
            this.models.imageInfo.bgImageData = data.image;
            this.models.imageInfo.hash = data.hash;
            this.hidePictureModal();
        },
        clearPicture() {
            this.models.imageInfo.bgImageData = [];
            this.models.imageInfo.hash = [];
        },
        hidePictureModal() {
            this.showPictureModal = false;
            this.focusScannedItem();
        },
        focusScannedItem() {
            this.$refs.scannedItem.$el.querySelector( 'input' ).focus();
        },
        async closeDetail() {
            if ( this.isPickupPhotoRequired && !this.hasPicture ) {
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
                this.$store.dispatch( 'snackbar', {
                    message: 'A Photo is required to complete this PickUp',
                    type: 'error',
                    duration: -1,
                } );
                return;
            }
            if ( !this.hasItems ) {
                await AudioUtil.playSound( soundNames.FAILURE_SHORT );
                this.$store.dispatch( 'snackbar', {
                    message: 'No Items have been scanned.',
                    type: 'error',
                    duration: 5000,
                } );
                return;
            }

            // Update all the scanned items
            this.inFlight = true;
            try {
                const { data } = await LogisticsApi.updateScannedPickupItems( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    mailItems: this.customerInfo.itemsList
                        .filter( ( scan ) => ( scan.scanned === true ) )
                        .map( ( item ) => item.id ) || null,
                    notes: this.models.notes || null,
                    photos: this.hasPicture
                        ? [ {
                            checkSum: this.models.imageInfo.hash[ 0 ].toString(),
                            data: this.models.imageInfo.bgImageData[ 0 ].toString(),
                        } ]
                        : [],
                    customerId: this.customerInfo.id,
                } );
                if ( ValidationUtil.arrayHasItems( data.mailTransWithMailTransItemsDTOList ) ) {
                    await AudioUtil.playSound( soundNames.SUCCESS_LONG );
                    this.$store.dispatch( 'snackbar', {
                        duration: 5000,
                        message: 'Pickup Complete!',
                        type: 'success',
                    } );
                    this.$router.push( {
                        name: 'MailPickup',
                    } );
                }
            } catch ( error ) {
                await AudioUtil.playSound( soundNames.FAILURE_LONG );
                this.$store.dispatch( 'snackbar', {
                    message: ErrorUtil.getMessageFromError( error ),
                    type: 'error',
                    duration: 5000,
                } );
            } finally {
                this.inFlight = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
#pickup-detail {
    margin-top: 1rem;
}
.customer-info {
    &__header {
        padding: 1rem;

        display: flex;
        justify-content: space-between;
    }
    &__scan {
        display: flex;
        align-items: center;
        & > * {
            margin-left: 1rem;
        }
    }
    &__details {
        p {
            margin: 0.5rem 0;
            &:first-child {
                margin-top: 0;
            }
        }
    }
    &__body {
        border-top: solid 1px $colors-grey-lighten-2;
        border-bottom: solid 1px $colors-grey-lighten-2;
    }
    &__footer {
        padding: 1rem;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        justify-items: center;
        align-items: center;
    }
    &__actions--left {
        justify-self: start;
        & > * {
            margin-right: 1rem;
        }
    }
    &__actions--right {
        justify-self: end;
    }
}
</style>
